import { type ClipType, type CreateMultipartResponse, type FileUpload, type UploadUrlResponse } from '@air/api/types';

export type InputFile = {
  readonly path?: string;
} & File;

export enum UploadStatus {
  /** This state is when urls are being fetched for an upload but the bytes for that actual file haven't started uploading yet */
  preparing = 'preparing',
  completed = 'completed',
  failed = 'failed',
  aborted = 'aborted',
  rejected = 'rejected',
  uploading = 'uploading',
  queued = 'queued',
  paused = 'paused',
  stopped = 'stopped',
}

type UploadData = {
  type: ClipType;
  boardRelativePath?: string;
  uploadGroupId?: string;
} & FileUpload;

export type RejectedUpload = {
  id: string;
  name: string;
  date: number;
  message: string;
  reason: string;
  status: UploadStatus.rejected;
};

type UploadSubBase = {
  clipId: string;
  s3Info: FileS3Info;
};

export type UploadBase = {
  id: string;
  batchId: string;
  file: FileData['file'];
  apiUploadData: FileData['apiUploadData'];
  createdAt: number;
  status: UploadStatus;
  errorMessage?: string;
  imageBase64?: string;
  isImageLoading?: boolean;
  isNonMedia: boolean;
  completedAt?: number;
  width?: number;
  height?: number;
} & UploadSubBase;

export type LargeUpload = {
  xhr?: { [partNumber: number]: XMLHttpRequest };
  s3Info: Omit<FileS3Info, 'uploadUrlInfo'> & {
    uploadUrlInfo?: CreateMultipartResponse[0];
  };
} & Omit<UploadBase, 's3Info'>;

export type SmallUpload = {
  xhr?: XMLHttpRequest;
  s3Info: Omit<FileS3Info, 'uploadUrlInfo'> & {
    uploadUrlInfo?: UploadUrlResponse[0];
  };
} & Omit<UploadBase, 's3Info'>;

export type Upload = LargeUpload | SmallUpload;

export type FileData = {
  custom: UploadSubBase;
  apiUploadData: UploadData;
  file: InputFile;
};

export type FileS3Info = {
  uploadUrlInfo?: CreateMultipartResponse[0] | UploadUrlResponse[0];
  totalBytesUploaded?: number;
  uploadedParts?: { ETag: string; PartNumber: number }[];
};

export type UploadProgressInfo = {
  progress: number;
  progressBeforePause: number;
  abort?: () => void;
  partsProgress?: { [partNumber: number]: number };
};

export type UploaderState = {
  uploadsArray: Upload[];
  rejectedUploads: RejectedUpload[];
  uploadProgress: { [uploadId: string]: UploadProgressInfo };
  currentUploadingInfo: {
    startedAt: number;
    uploadedBytes: number;
    /**
     * If set, this number represents the number of bytes/second the user's upload speed is currently at
     */
    speed: number | null;
  };
};

export const initialUploadProgress: UploadProgressInfo = {
  progress: 0,
  progressBeforePause: 0,
};

export type UploadPart = {
  ETag: string;
  PartNumber: number;
};

export type UploaderStore = { uploader: UploaderState };

export type LargeUploadWithS3 = Omit<LargeUpload, 's3Info'> & {
  s3Info: Omit<LargeUpload['s3Info'], 'uploadUrlInfo'> & Required<Pick<LargeUpload['s3Info'], 'uploadUrlInfo'>>;
};
